import { useMemo } from 'react';
import { usePracticeNames } from '../../hooks/usePracticeNames';
import { PracticeMultiSelect, type PracticeRow } from '../shared/PracticeMultiSelect';
import type { AudienceMode } from './eventDraft';
import { FIELD_LABEL_CLASS, type SectionProps } from './eventFormShared';

interface AudienceOption {
    readonly value: AudienceMode;
    readonly label: string;
    readonly hint: string;
}

const AUDIENCE_OPTIONS: readonly AudienceOption[] = [
    {
        value: 'all',
        label: 'Every practice',
        hint: 'Anyone who logs in sees the banner.'
    },
    {
        value: 'selected',
        label: 'Selected practices',
        hint: 'Only users attached to a practice below see it.'
    }
];

/** Limits who sees the banner. Empty `practices` on the wire means "everyone",
 *  so the mode is authored explicitly rather than inferred from the list being
 *  empty - see `audienceMode` in eventDraft.ts. */
export function AudienceSection({ draft, set, errors, touched, canManage }: SectionProps) {
    const isSelected = draft.audienceMode === 'selected';

    // The draft stores ids; the picker wants rows. A name that fails to resolve
    // falls back to the raw id inside PracticeMultiSelect, so a practice that
    // has gone away is still visible and removable rather than silently dropped.
    const { practices: namedPractices } = usePracticeNames(draft.practices);
    const selectedRows = useMemo<PracticeRow[]>(
        () => namedPractices.map(({ id, name }) => ({ id, name })),
        [namedPractices]
    );

    const handleChange = (next: PracticeRow[]) => {
        set(
            'practices',
            next.map((practice) => practice.id)
        );
    };

    return (
        <div className="flex flex-col gap-4">
            <div>
                <label className={FIELD_LABEL_CLASS}>Who sees this event</label>
                <div className="flex flex-col gap-2">
                    {AUDIENCE_OPTIONS.map((option) => (
                        <label
                            key={option.value}
                            className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 transition-colors ${
                                draft.audienceMode === option.value
                                    ? 'border-gray-800 bg-gray-50'
                                    : 'border-gray-300 bg-white'
                            } ${canManage ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                        >
                            <input
                                type="radio"
                                name="event-audience-mode"
                                value={option.value}
                                checked={draft.audienceMode === option.value}
                                // Only the mode changes - `practices` is left alone so
                                // toggling to All and back does not discard the list.
                                // `audiencePractices` is what makes All send [] anyway.
                                onChange={() => set('audienceMode', option.value)}
                                className="mt-0.5 h-4 w-4 text-gray-800 focus:ring-gray-400"
                            />
                            <span className="min-w-0">
                                <span className="block text-[13px] font-semibold text-gray-800">
                                    {option.label}
                                </span>
                                <span className="block text-[11px] text-gray-400">
                                    {option.hint}
                                </span>
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {isSelected && (
                <div>
                    <label className={FIELD_LABEL_CLASS}>Practices *</label>
                    <PracticeMultiSelect
                        selected={selectedRows}
                        onChange={handleChange}
                        placeholder="Search practices to add…"
                        emptyText="No practices yet - this event would reach no one."
                    />
                    {touched && errors.practices ? (
                        <p className="text-red-500 text-xs mt-1">{errors.practices}</p>
                    ) : (
                        <p className="text-[11px] text-gray-400 mt-1">
                            A user attached to any one of these practices sees the banner, and
                            nobody else does. It rotates with whatever other events are live at the
                            same time.
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
